import {
	faBullseye as fasBullseye,
	faMoneyBill1 as fasMoneyBill1,
	faHandHoldingDollar as fasHandHoldingDollar,
	faFloppyDisk as fasFloppyDisk,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { PromisePool } from "bm-ui-kit";
import type { FormApi } from "final-form";
import createDecorator from "final-form-focus";
import React from "react";
import type { ReactNode } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import { Form as FinalForm } from "react-final-form";
import { Button } from "../../Button";
import { FormCurrency } from "../../FormCurrency";
import type { FormSelectOptions } from "../../FormSelect";
import { FormSelect } from "../../FormSelect";
import type { FormLoanSimpleData } from "./FormLoanSimpleItem.data";
import { LOAN_TYPES } from "./FormLoanSimpleItem.data";

const focusOnError = createDecorator();

const MIN_DEBT = 5_000;
const MAX_DEBT = 1_000_000;

interface Props {
	show: boolean;
	initialValues?: FormLoanSimpleData;
	onSave(loan: FormLoanSimpleData): void;
	onHide(): void;
}

interface State {
	loanTypes: FormSelectOptions;
}

export class FormLoanSimpleModal extends React.Component<Props, State> {
	private readonly promisePool = new PromisePool();

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	private renderChildren(formApi: FormApi<FormLoanSimpleData>): ReactNode {
		const { values } = formApi.getState();
		const { loanType } = values;

		return (
			<Row>
				<Col md={6} sm={7}>
					<FormSelect
						label="Hitel típus"
						name="loanType"
						options={LOAN_TYPES}
						prepend={<FontAwesomeIcon icon={fasBullseye} />}
						placeholder="Válassz ki egy hitel típus!"
						required
					/>
				</Col>
				<Col md={6} sm={5}>
					<FormCurrency
						label={["MORTGAGE", "PERSONAL_LOAN"].includes(loanType) ? "Havi törlesztő" : "Hitel keret"}
						name="debt"
						prepend={<FontAwesomeIcon icon={fasMoneyBill1} />}
						validate={this.validDebt}
						required
					/>
				</Col>
			</Row>
		);
	}

	public render(): ReactNode {
		const { show, initialValues } = this.props;

		return (
			<FinalForm<FormLoanSimpleData>
				initialValues={initialValues}
				decorators={[focusOnError]}
				onSubmit={this.handleSubmit}
			>
				{({ form }): ReactNode => (
					<Modal centered show={show} onHide={(): void => this.handleHide(form)}>
						<Modal.Header closeButton>
							<Modal.Title className="mb-0">
								<FontAwesomeIcon icon={fasHandHoldingDollar} className="mx-2" /> Meglévő hitelek
							</Modal.Title>
						</Modal.Header>
						<Modal.Body className="p-3">{this.renderChildren(form)}</Modal.Body>
						<Modal.Footer>
							<Button
								type="button"
								onClick={form.submit}
								className={form.getState().valid ? "" : "disabled"}
								icon={<FontAwesomeIcon icon={fasFloppyDisk} />}
							>
								Mentés
							</Button>
						</Modal.Footer>
					</Modal>
				)}
			</FinalForm>
		);
	}

	@autobind
	private handleSubmit(values: FormLoanSimpleData, formApi: FormApi<FormLoanSimpleData>): void {
		const { onSave } = this.props;
		const newValue = { ...values };
		newValue.loanTypeName = this.getLoanName(values.loanType);

		this.promisePool.addTimeout(0).then(formApi.reset);
		onSave(values);
	}

	@autobind
	private handleHide(formApi: FormApi<FormLoanSimpleData>): void {
		const { onHide } = this.props;

		this.promisePool.addTimeout(0).then(formApi.reset);
		onHide();
	}

	private getLoanName(loanType: string): string {
		return LOAN_TYPES.find((loan) => loan.value === loanType)?.label;
	}

	@autobind
	private validDebt(amount: number): string {
		if (isNaN(amount)) {
			return "A tartozás megadása kötelező!";
		}

		if (amount < MIN_DEBT) {
			return `A megadható legalacsonyabb tartozás ${formatText(MIN_DEBT, "ft")}!`;
		}

		if (amount > MAX_DEBT) {
			return `A megadható legmagasabb tartozás ${formatText(MAX_DEBT, "ft")}!`;
		}
	}
}
